import React, { useEffect, useState } from "react";
import * as translator from "../../../../../utils/translator";
import { MyAccountStoreProduct } from "../types";
import MyAccountStoreProductCard from "./MyAccountStoreProductCard";

interface Props {
  language: string;
  translationPrefix: string;
  products: MyAccountStoreProduct[];
  tagDimensions: string[];
  /**
   * How many cards to show before the "Vollständige Liste" button. The list
   * arrives already personalized (PRD §7), so the first N are the most
   * relevant. When 0 or >= products, the whole list is shown and the button
   * is hidden.
   */
  collapsedCount: number;
  /** When the user is filtering, the full (filtered) list is always shown. */
  hasActiveFilters: boolean;
  orderingProductID: number | null;
  onOrderNow: (product: MyAccountStoreProduct) => void;
  onMoreInfo: (product: MyAccountStoreProduct) => void;
  onTagClick: (product: MyAccountStoreProduct) => void;
}

const MyAccountStoreProductGrid: React.FC<Props> = ({
  language,
  translationPrefix,
  products,
  tagDimensions,
  collapsedCount,
  hasActiveFilters,
  orderingProductID,
  onOrderNow,
  onMoreInfo,
  onTagClick,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  // Collapse again whenever the personalized head changes (e.g. data reload).
  useEffect(() => {
    setExpanded(false);
  }, [collapsedCount]);

  if (products.length === 0) {
    return (
      <div className="bg-white border border-dashed border-gray-300 rounded-2xl py-12 px-4 text-center mb-8">
        <p className="text-gray-500">
          {translator.translate(language, "my_account", `${translationPrefix}/grid/empty`)}
        </p>
      </div>
    );
  }

  const canCollapse =
    !hasActiveFilters && collapsedCount > 0 && products.length > collapsedCount;
  const showAll = expanded || !canCollapse;
  const visibleProducts = showAll ? products : products.slice(0, collapsedCount);

  return (
    <div className="mb-8">
      <div className="flex flex-col gap-4">
        {visibleProducts.map((product) => (
          <MyAccountStoreProductCard
            key={product.productID}
            language={language}
            translationPrefix={translationPrefix}
            product={product}
            tagDimensions={tagDimensions}
            isOrdering={orderingProductID === product.productID}
            onOrderNow={onOrderNow}
            onMoreInfo={onMoreInfo}
            onTagClick={onTagClick}
          />
        ))}
      </div>

      {canCollapse && !expanded && (
        <button
          type="button"
          className="mt-6 flex h-[56px] w-full sm:w-[280px] cursor-pointer appearance-none items-center justify-center rounded-[4px] border-0 px-[32px] text-[18px] font-medium leading-none text-white transition-opacity hover:opacity-90"
          style={{ backgroundImage: "linear-gradient(90deg, #15967c 0%, #72b33e 100%)" }}
          onClick={() => setExpanded(true)}
        >
          {translator.translate(language, "my_account", `${translationPrefix}/grid/allList`)}
        </button>
      )}
    </div>
  );
};

export default MyAccountStoreProductGrid;
